import React, { useState } from 'react';
import { validateEmail, validatePassword, getPasswordStrength, validateRequired } from '../utils/validation';
import { sentry } from '../services/sentry';
import { trackLinkedInConversion, LINKEDIN_CONVERSIONS } from '../services/linkedin';
import axios from 'axios';
import './SignupPage.css';
import { getApiBaseUrl } from '../utils/env';

const API_BASE_URL = getApiBaseUrl();

function SignupPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    accountName: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    phoneNumber: '',
    typeOfBusiness: '',
    aiPainPoints: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, formData[name]);
  };

  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'firstName':
      case 'lastName':
      case 'companyName':
      case 'accountName':
        if (!validateRequired(value)) {
          error = 'This field is required';
        }
        break;

      case 'username':
        if (!validateRequired(value)) {
          error = 'Username is required';
        }
        break;

      case 'email':
        if (!validateRequired(value)) {
          error = 'Email is required';
        } else if (!validateEmail(value)) {
          error = 'Please enter a valid email address';
        }
        break;

      case 'password':
        if (!validateRequired(value)) {
          error = 'Password is required';
        } else {
          const validation = validatePassword(value);
          if (!validation.isValid) {
            error = 'Password must be at least 8 characters with 1 uppercase, 1 number, and 1 special character';
          }
        }
        break;

      case 'confirmPassword':
        if (!validateRequired(value)) {
          error = 'Please confirm your password';
        } else if (value !== formData.password) {
          error = 'Passwords do not match';
        }
        break;

      default:
        break;
    }

    setErrors(prev => ({ ...prev, [name]: error }));
    return error === '';
  };

  const validateForm = () => {
    const requiredFields = ['firstName', 'lastName', 'username', 'email', 'password', 'confirmPassword', 'companyName', 'accountName'];
    let isValid = true;
    const newErrors = {};

    requiredFields.forEach(field => {
      if (!validateField(field, formData[field])) {
        isValid = false;
      }
    });

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    // Mark all fields as touched
    const allTouched = {};
    Object.keys(formData).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/signup`, formData);

      if (response.data.success) {
        trackLinkedInConversion(LINKEDIN_CONVERSIONS.SIGNUP);
        setSubmitted(true);
      } else {
        const requestId = response.headers['x-request-id'] || 'unknown';
        const errorMsg = response.data.error || 'Registration failed. Please try again.';
        setServerError(`${errorMsg} (Error ID: ${requestId})`);
        
        // Log to Sentry placeholder
        sentry.captureException(new Error(errorMsg), {
          requestId,
          context: 'signup',
          formData: { ...formData, password: '[REDACTED]', confirmPassword: '[REDACTED]' }
        });
      }
    } catch (error) {
      const requestId = error.response?.headers['x-request-id'] || 
                        error.config?.headers['X-Request-ID'] || 
                        'unknown';
      
      let errorMsg = 'Network error. Please check your connection and try again.';
      
      if (error.response) {
        // Server responded with error
        errorMsg = error.response.data?.error || error.response.data?.message || errorMsg;
      }
      
      setServerError(`${errorMsg} (Error ID: ${requestId})`);
      console.error(`[${requestId}] Signup failed:`, error);
      
      // Log to Sentry placeholder
      sentry.captureException(error, {
        requestId,
        context: 'signup',
        statusCode: error.response?.status,
        formData: { ...formData, password: '[REDACTED]', confirmPassword: '[REDACTED]' }
      });
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(formData.password);

  if (submitted) {
    return (
      <div className="signup-page">
        <div className="signup-container">
          <div className="success-card">
            <div className="success-icon">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="32" fill="#10B981"/>
                <path d="M20 32 L28 40 L44 24" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1>Account Created Successfully!</h1>
            <p className="success-message">
              We've sent an activation link to <strong>{formData.email}</strong>.
              Please check your email and click the link to activate your account.
            </p>
            <div className="success-info">
              <p>📧 Check your spam folder if you don't see the email</p>
              <p>⏰ The activation link will expire in 24 hours</p>
            </div>
            <a href="/login" className="btn-primary btn-large">
              Go to Login
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="signup-header">
          <a href="/" className="signup-logo" aria-label="CerebralMachines home">
            <img src="/CMLogo.png" alt="CerebralMachines" />
          </a>
          <h1>Create Your Account</h1>
          <p>Join the future of enterprise AI</p>
        </div>

        {serverError && (
          <div className="alert alert-error">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <circle cx="10" cy="10" r="9"/>
              <path d="M10 6 L10 11" stroke="white" strokeWidth="2"/>
              <circle cx="10" cy="14" r="1" fill="white"/>
            </svg>
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="signup-form">
          {/* Personal Information */}
          <div className="form-section">
            <h2 className="section-title">Personal Information</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">
                  First Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={touched.firstName && errors.firstName ? 'error' : ''}
                />
                {touched.firstName && errors.firstName && (
                  <span className="error-message">{errors.firstName}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="lastName">
                  Last Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={touched.lastName && errors.lastName ? 'error' : ''}
                />
                {touched.lastName && errors.lastName && (
                  <span className="error-message">{errors.lastName}</span>
                )}
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="form-section">
            <h2 className="section-title">Account Information</h2>
            
            <div className="form-group">
              <label htmlFor="username">
                Username <span className="required">*</span>
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                onBlur={handleBlur}
                className={touched.username && errors.username ? 'error' : ''}
              />
              {touched.username && errors.username && (
                <span className="error-message">{errors.username}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Email Address <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className={touched.email && errors.email ? 'error' : ''}
              />
              {touched.email && errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="password">
                Password <span className="required">*</span>
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                className={touched.password && errors.password ? 'error' : ''}
              />
              {formData.password && (
                <div className="password-strength">
                  <div className="strength-bar">
                    <div 
                      className="strength-fill"
                      style={{ 
                        width: `${(passwordStrength.score / 5) * 100}%`,
                        backgroundColor: passwordStrength.color
                      }}
                    />
                  </div>
                  <span style={{ color: passwordStrength.color }}>
                    {passwordStrength.strength}
                  </span>
                </div>
              )}
              {touched.password && errors.password && (
                <span className="error-message">{errors.password}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">
                Confirm Password <span className="required">*</span>
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                className={touched.confirmPassword && errors.confirmPassword ? 'error' : ''}
              />
              {formData.confirmPassword && formData.password === formData.confirmPassword && (
                <span className="success-message">✓ Passwords match</span>
              )}
              {touched.confirmPassword && errors.confirmPassword && (
                <span className="error-message">{errors.confirmPassword}</span>
              )}
            </div>
          </div>

          {/* Company Information */}
          <div className="form-section">
            <h2 className="section-title">Company Information</h2>
            
            <div className="form-group">
              <label htmlFor="companyName">
                Company Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                onBlur={handleBlur}
                className={touched.companyName && errors.companyName ? 'error' : ''}
              />
              {touched.companyName && errors.companyName && (
                <span className="error-message">{errors.companyName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="accountName">
                Account Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="accountName"
                name="accountName"
                value={formData.accountName}
                onChange={handleChange}
                onBlur={handleBlur}
                className={touched.accountName && errors.accountName ? 'error' : ''}
              />
              {touched.accountName && errors.accountName && (
                <span className="error-message">{errors.accountName}</span>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">City</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="state">State/Province</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="country">Country</label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="zipCode">ZIP/Postal Code</label>
                <input
                  type="text"
                  id="zipCode"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phoneNumber">Phone Number</label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="typeOfBusiness">Type of Business</label>
                <select
                  id="typeOfBusiness"
                  name="typeOfBusiness"
                  value={formData.typeOfBusiness}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  <option value="healthcare">Healthcare & Life Sciences</option>
                  <option value="financial">Financial Services</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="retail">Retail & E-commerce</option>
                  <option value="technology">Technology</option>
                  <option value="legal">Legal & Compliance</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="aiPainPoints">Current AI Pain Points</label>
              <textarea
                id="aiPainPoints"
                name="aiPainPoints"
                value={formData.aiPainPoints}
                onChange={handleChange}
                rows="4"
                placeholder="Tell us about the AI challenges you're facing..."
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="btn-primary btn-large btn-full"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          <div className="form-footer">
            Already have an account? <a href="/login">Sign In</a>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SignupPage;

